import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPinOff } from 'lucide-react';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { SpawnRow } from '../../components/world/SpawnRow';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { MonsterItemDrops } from '../../components/world/MonsterItemDrops';
import { EventPositionList } from '../../components/world/EventPositionList';
import { NpcCard } from '../../components/progression/NpcCard';
import { npcs } from '../../data/guide/npcs';
import { monsterKind } from '../../utils/monsterKind';
import { useEventPositions } from '../../hooks/useEventPositions';
import { positionsFor } from '../../utils/eventPositions';
import { EntityImage } from '../../components/world/EntityImage';
import { imageEntryForMonster } from '../../utils/entityImages';

/** Posiciones del monstruo dentro de eventos (32_event_spawn_rules), separadas de los spots permanentes. */
function MonsterEventPositions({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const ep = useEventPositions();
  const list = ep ? positionsFor(ep.positions, monsterId) : [];
  if (!list.length) return null;
  return <WorldSection id="posiciones-evento" title="Posiciones dentro de eventos" count={list.length} data-id="element-2661" data-mp="1gf994kphy99x">
      <EventPositionList w={w} positions={list} max={12} data-id="element-2662" data-mp="1leu8uxchqlnp" />
    </WorldSection>;
}
import { BoxContents } from '../../components/world/BagSourceList';
import { monsterBags } from '../../utils/bagSources';
import { mapName, mapPath, formatSeconds } from '../../utils/worldFormat';
import type { Monster, WorldData } from '../../types/world';
export function MonsterDetail() {
  const {
    monsterId
  } = useParams();
  useHashScroll();
  return <WorldGate data-id="element-2663" data-mp="11p9r49ru4gvm">
      {w => {
      const m = w.monsters.get(Number(monsterId));
      if (!m) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2664" data-mp="wr85bh30y3gg">
              <p className="text-white" data-id="element-2665" data-mp="1apzjt4lstr54">No encontramos ese monstruo en los archivos.</p>
              <Link to="/guia/mapas?vista=monstruos" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2666" data-mp="15rxy0bwzndpy">Volver a Monstruos</Link>
            </div>;
      }
      return <MonsterView w={w} m={m} data-id="element-2667" data-mp="e19yt5evkiex" />;
    }}
    </WorldGate>;
}
function MonsterView({
  w,
  m
}: {
  w: WorldData;
  m: Monster;
}) {
  useEffect(() => {
    document.title = `${m.name} · Monstruos · Guía MU Vanguard`;
  }, [m.name]);
  const locations = w.idx.locationsByMonster.get(m.id) ?? [];
  // Solo NPC con identidad confirmada (monsterId); los candidatos de Pet Trainer no reciben su tarjeta.
  const guideNpc = npcs.find(n => n.monsterId === m.id);
  const permanent = locations.filter(l => l.kind === 'ordinary');
  const temporary = locations.filter(l => l.kind !== 'ordinary');
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? [];
  const spots = spawns.filter(s => s.kind === 'spot');
  const invasionSpawns = spawns.filter(s => s.kind === 'invasion');
  const eventIds = Array.from(new Set([...locations, ...spawns].map(x => x.eventId).filter((v): v is string => !!v)));
  return <div data-id="element-2668" data-mp="2pon1zp9m4s51">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: 'Monstruos',
      to: '/guia/mapas?vista=monstruos'
    }, {
      label: m.name
    }]} eyebrow={guideNpc || monsterKind(w, m) === 'npc' ? 'NPC' : 'Monstruo'} title={m.name} media={imageEntryForMonster(w, m.id)?.image ? <EntityImage entry={imageEntryForMonster(w, m.id)} alt={m.name} name={m.name} size="hero" lazy={false} kind={guideNpc ? 'persona' : 'monstruo'} data-id="element-2670" data-mp="2knfttp3k5qxu" /> : undefined} data-id="element-2669" data-mp="2pntycx5yair4">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2671" data-mp="19awus9n75nr6">
          {m.level !== undefined && <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-2672" data-mp="1okk00lv7dgpb">Nivel del monstruo (catálogo): {m.level}</span>}
          <CopyLinkButton label="Copiar enlace" data-id="element-2673" data-mp="1fs8d39zasbe3" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-2674" data-mp="1ateytfc6sgcv">
        <WorldSection id="ubicaciones" title="Dónde aparece" data-id="element-2675" data-mp="1au6s2jiba1zz">
          {guideNpc ?
        // NPC de las guías: misma resolución de ubicación que en misiones, recetas y buscador.
        <NpcCard npcId={guideNpc.id} data-id="element-2676" data-mp="23hj96e5pdhlw" /> : locations.length === 0 ? <p className="flex items-start gap-2 rounded-2xl border border-white/10 p-4 text-gray-300" data-id="element-2677" data-mp="1ygn2xtxvpjdb">
              <MapPinOff className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-2678" data-mp="1ti24o026t9ri" />
              Ubicación no documentada en los archivos disponibles.
            </p> : <div className="grid gap-6 md:grid-cols-2" data-id="element-2679" data-mp="i2wyun6rev3o">
              {[{
            title: 'Presencia permanente',
            list: permanent
          }, {
            title: 'Solo durante eventos o invasiones',
            list: temporary
          }].map(g => g.list.length > 0 && <div key={g.title} data-id="element-2680" data-mp="hxe4envesren">
                      <h3 className="mb-2 text-sm font-semibold text-gray-400" data-id="element-2681" data-mp="hwctcjee84wx">{g.title}</h3>
                      <ul className="space-y-2" data-id="element-2682" data-mp="mpfo2uuwpkep">
                        {g.list.map(l => <li key={l.id} className="flex flex-wrap items-center gap-2" data-id="element-2683" data-mp="qpg2wglx3xma">
                            <Link to={mapPath(l.mapId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-2684" data-mp="voavubdln2k4">
                              {mapName(w, l.mapId)}
                            </Link>
                            {l.eventId && <EventChip w={w} eventId={l.eventId} data-id="element-2685" data-mp="32g1wpk3w8txu" />}
                          </li>)}
                      </ul>
                    </div>)}
            </div>}
        </WorldSection>

        <WorldSection id="spots" title="Spots y áreas" count={spots.length} data-id="element-2686" data-mp="2zefhyw3gwaag">
          {spots.length ? <ul className="grid gap-2.5" data-id="element-2687" data-mp="2uh70kxq58aa9">{spots.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2688" data-mp="39cmy5cpxmobe" />)}</ul> : <p className="text-gray-400" data-id="element-2689" data-mp="34djsbq24oe2d">No hay spots permanentes configurados para este monstruo.</p>}
        </WorldSection>

        <MonsterEventPositions w={w} monsterId={m.id} data-id="element-2690" data-mp="3ibzztj7te8lk" />

        {invasionSpawns.length > 0 && <WorldSection id="invasiones" title="Apariciones en invasiones" count={invasionSpawns.length} data-id="element-2691" data-mp="3dcx08ealsukz">
            <p className="mb-3 text-sm text-gray-400" data-id="element-2692" data-mp="3s1jj2pzfw4gy">Cada fila es un registro. Las filas de mapas alternativos no son monstruos adicionales.</p>
            <ul className="grid gap-2.5" data-id="element-2693" data-mp="4y4yof0espqx">{invasionSpawns.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-2694" data-mp="4yy5ffzdbf92" />)}</ul>
          </WorldSection>}

        {eventIds.length > 0 && <WorldSection id="eventos" title="Eventos e invasiones relacionados" data-id="element-2695" data-mp="uqinoch09dh">
            <div className="flex flex-wrap gap-2" data-id="element-2696" data-mp="jln3rehvvmdv">{eventIds.map(id => <EventChip key={id} w={w} eventId={id} data-id="element-2697" data-mp="ok6uds7f9mck" />)}</div>
          </WorldSection>}

        <MonsterItemDrops w={w} monsterId={m.id} data-id="element-2698" data-mp="akys2qfypy45" />

        <BoxContents w={w} bags={monsterBags(w, m.id)} id="recompensas" title="Recompensas posibles al derrotarlo" intro="Lista interna de resultados posibles al derrotar a este monstruo. No es una caja que abras ni se entregan todos juntos." data-id="element-2699" data-mp="am9tijlxqts3" />

        <Disclosure title="Estadísticas de catálogo" data-id="element-2700" data-mp="5mx905ijdyso">
          <StatList rows={[{
          label: 'Nivel (catálogo)',
          value: m.level
        }, {
          label: 'Vida (catálogo)',
          value: m.hp
        }, {
          label: 'Daño mínimo (catálogo)',
          value: m.damageMin
        }, {
          label: 'Daño máximo (catálogo)',
          value: m.damageMax
        }, {
          label: 'Defensa (catálogo)',
          value: m.defense
        }, {
          label: 'Reaparición (catálogo)',
          value: m.respawnSeconds !== undefined ? formatSeconds(m.respawnSeconds) : undefined
        }, {
          label: 'item_rate_divisor',
          value: m.itemRateDivisor,
          note: 'no es una probabilidad de drop'
        }, {
          label: 'ID del monstruo',
          value: m.id
        }]} data-id="element-2701" data-mp="3o03f7lgy4yp2" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-2702" data-mp="3kh6054nbjuv0">Valores generales del catálogo. Cada spot puede tener estadísticas configuradas distintas; míralas en la ficha del spot.</p>
        </Disclosure>
      </div>
    </div>;
}